'use client';

// P29.4 / Q7: sticky marketing nav with framer-motion shrink-on-scroll. Uses
// `m` (LazyMotion-aware motion component) instead of `motion` so the bundle
// stays small via LazyMotionProvider in (marketing)/layout.tsx.

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { m, useReducedMotion } from 'framer-motion';
import { useLocale, useTranslations } from 'next-intl';
import { LanguageToggle } from './LanguageToggle';

const SHRINK_AT_PX = 100;

export function MarketingHeader() {
  const t = useTranslations('marketing.header');
  const locale = useLocale();
  const reduceMotion = useReducedMotion();
  const [shrunk, setShrunk] = useState(false);

  useEffect(() => {
    function onScroll() {
      setShrunk(window.scrollY > SHRINK_AT_PX);
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const localePrefix = `/${locale}`;

  return (
    <m.header
      data-testid="marketing-header"
      initial={false}
      animate={
        reduceMotion
          ? { height: 80, boxShadow: 'none' }
          : { height: shrunk ? 60 : 80, boxShadow: shrunk ? '0 1px 8px rgb(0 0 0 / 0.08)' : 'none' }
      }
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="sticky top-0 z-30 flex w-full items-center border-b border-slate-200 bg-white/90 backdrop-blur"
    >
      <div className="mx-auto flex w-full max-w-6xl items-center justify-between gap-4 px-4">
        <Link
          href={`${localePrefix}/`}
          data-testid="marketing-header-logo"
          className="flex items-center gap-2 text-sm font-semibold tracking-tight"
        >
          <span aria-hidden className="text-emerald-700">
            ●
          </span>{' '}
          CateringOS
        </Link>
        <nav
          className="hidden items-center gap-6 text-sm text-slate-600 md:flex"
          aria-label={t('navAria')}
        >
          <a href="#features" className="hover:text-emerald-700">
            {t('navFeatures')}
          </a>
          <a href="#pricing" className="hover:text-emerald-700">
            {t('navPricing')}
          </a>
          <a href="#faq" className="hover:text-emerald-700">
            {t('navFaq')}
          </a>
        </nav>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <Link
            href={`${localePrefix}/login`}
            data-testid="marketing-header-login"
            className="rounded-md bg-emerald-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-800"
          >
            {t('login')}
          </Link>
        </div>
      </div>
    </m.header>
  );
}
